import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddAssigneesRequest,
  AddProgressUpdate,
  AnalyticsQuery,
  AssignTeamRequest,
  AssignWorkOrder,
  ChangeAssigneeRoleRequest,
  ChangeWorkOrderStatus,
  CreateTeam,
  CreateWorkOrder,
  EscalateWorkOrder,
  PreviewRecurrenceRequest,
  UpdateWorkOrder,
  WorkOrdersQuery,
  WorkOrdersState,
} from "../models/models";
import workOrdersApi from "../api/workOrdersApi";
import teamsApi from "../api/teamsApi";

// ─── Thunks ───────────────────────────────────────────────────

export const fetchWorkOrders = createAsyncThunk(
  "workOrdersSlice/fetchWorkOrders",
  async (query: WorkOrdersQuery) => {
    return workOrdersApi.getWorkOrders(query);
  }
);

export const fetchWorkOrder = createAsyncThunk(
  "workOrdersSlice/fetchWorkOrder",
  async (id: string) => {
    return workOrdersApi.getWorkOrder(id);
  }
);

export const createWorkOrder = createAsyncThunk(
  "workOrdersSlice/createWorkOrder",
  async (model: CreateWorkOrder) => {
    return workOrdersApi.createWorkOrder(model);
  }
);

export const updateWorkOrder = createAsyncThunk(
  "workOrdersSlice/updateWorkOrder",
  async ({ id, model }: { id: string; model: UpdateWorkOrder }) => {
    return workOrdersApi.updateWorkOrder(id, model);
  }
);

export const changeWorkOrderStatus = createAsyncThunk(
  "workOrdersSlice/changeWorkOrderStatus",
  async ({ id, model }: { id: string; model: ChangeWorkOrderStatus }) => {
    return workOrdersApi.changeStatus(id, model);
  }
);

// WO-WORKER-EDIT-NOTES: Edit closing notes + cost while WO is PendingReview.
// Dedicated endpoint (not /status) — no status transition, doesn't fire status
// change notifications. Caller reloads detail on fulfilled.
export const editCompletionDetails = createAsyncThunk(
  "workOrdersSlice/editCompletionDetails",
  async ({
    id,
    completionNotes,
    cost,
  }: {
    id: string;
    completionNotes: string;
    cost: number | undefined;
  }) => {
    await workOrdersApi.editCompletionDetails(id, completionNotes, cost);
    return id;
  }
);

export const assignWorkOrder = createAsyncThunk(
  "workOrdersSlice/assignWorkOrder",
  async ({ id, model }: { id: string; model: AssignWorkOrder }) => {
    return workOrdersApi.assignWorkOrder(id, model);
  }
);

export const addWorkOrderProgressUpdate = createAsyncThunk(
  "workOrdersSlice/addWorkOrderProgressUpdate",
  async ({ id, model }: { id: string; model: AddProgressUpdate }) => {
    return workOrdersApi.addProgressUpdate(id, model);
  }
);

export const fetchWorkOrderSummary = createAsyncThunk(
  "workOrdersSlice/fetchWorkOrderSummary",
  async () => {
    return workOrdersApi.getSummary();
  }
);

export const fetchWorkOrderFilters = createAsyncThunk(
  "workOrdersSlice/fetchWorkOrderFilters",
  async () => {
    return workOrdersApi.getFilters();
  }
);

export const fetchMyTasks = createAsyncThunk(
  "workOrdersSlice/fetchMyTasks",
  async () => {
    return workOrdersApi.getMyTasks();
  }
);

export const generateRecurring = createAsyncThunk(
  "workOrdersSlice/generateRecurring",
  async () => {
    return workOrdersApi.generateRecurring();
  }
);

export const previewRecurrence = createAsyncThunk(
  "workOrdersSlice/previewRecurrence",
  async (request: PreviewRecurrenceRequest) => {
    return workOrdersApi.previewRecurrence(request);
  }
);

export const escalateWorkOrder = createAsyncThunk(
  "workOrdersSlice/escalateWorkOrder",
  async ({ id, model }: { id: string; model: EscalateWorkOrder }) => {
    return workOrdersApi.escalateToServiceBoard(id, model);
  }
);

// ─── Recurring series thunks ────────────────────────────────

export const regenerateFutureInstances = createAsyncThunk(
  "workOrdersSlice/regenerateFutureInstances",
  async (templateId: string) => {
    return workOrdersApi.regenerateFutureInstances(templateId);
  }
);

export const cancelFutureInstances = createAsyncThunk(
  "workOrdersSlice/cancelFutureInstances",
  async (templateId: string) => {
    return workOrdersApi.cancelFutureInstances(templateId);
  }
);

// ─── KPI Summary thunk ──────────────────────────────────────

export const fetchKpiSummary = createAsyncThunk(
  "workOrdersSlice/fetchKpiSummary",
  async () => {
    return workOrdersApi.getKpiSummary();
  }
);

// ─── Phase 3A: Schedule calendar thunk ──────────────────────

export const fetchScheduleWorkOrders = createAsyncThunk(
  "workOrdersSlice/fetchScheduleWorkOrders",
  async (query: { dateFrom: string; dateTo: string }) => {
    return workOrdersApi.getWorkOrders({
      pageNumber: 1,
      pageSize: 500, // Calendar shows all in range, no pagination
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
    });
  }
);

// ─── Phase 3C: Analytics thunk ──────────────────────────────

export const fetchAnalytics = createAsyncThunk(
  "workOrdersSlice/fetchAnalytics",
  async (query: AnalyticsQuery) => {
    return workOrdersApi.getAnalytics(query);
  }
);

// ─── Phase 3B: Team thunks ──────────────────────────────────

export const fetchTeams = createAsyncThunk(
  "workOrdersSlice/fetchTeams",
  async (locationId?: string) => {
    return teamsApi.getTeams(locationId);
  }
);

// RC-5: Location-filtered assignable users
export const fetchAssignableUsers = createAsyncThunk(
  "workOrdersSlice/fetchAssignableUsers",
  async (locationId?: string) => {
    return teamsApi.getAssignableUsers(locationId);
  }
);

export const createTeam = createAsyncThunk(
  "workOrdersSlice/createTeam",
  async (model: CreateTeam) => {
    return teamsApi.createTeam(model);
  }
);

export const updateTeam = createAsyncThunk(
  "workOrdersSlice/updateTeam",
  async ({ id, model }: { id: string; model: CreateTeam }) => {
    return teamsApi.updateTeam(id, model);
  }
);

export const deleteTeam = createAsyncThunk(
  "workOrdersSlice/deleteTeam",
  async (id: string) => {
    await teamsApi.deleteTeam(id);
    return id;
  }
);

export const addTeamMember = createAsyncThunk(
  "workOrdersSlice/addTeamMember",
  async ({ teamId, model }: { teamId: string; model: { userId: string; userName: string } }) => {
    return teamsApi.addMember(teamId, model);
  }
);

export const removeTeamMember = createAsyncThunk(
  "workOrdersSlice/removeTeamMember",
  async ({ teamId, userId }: { teamId: string; userId: string }) => {
    await teamsApi.removeMember(teamId, userId);
    return { teamId, userId };
  }
);

export const fetchTeamWorkload = createAsyncThunk(
  "workOrdersSlice/fetchTeamWorkload",
  async (teamId: string) => {
    return teamsApi.getWorkload(teamId);
  }
);

// RC-6: Team location thunks
export const fetchTeamLocations = createAsyncThunk(
  "workOrdersSlice/fetchTeamLocations",
  async (teamId: string) => {
    return teamsApi.getTeamLocations(teamId);
  }
);

export const updateTeamLocations = createAsyncThunk(
  "workOrdersSlice/updateTeamLocations",
  async ({ teamId, locationIds }: { teamId: string; locationIds: string[] }) => {
    await teamsApi.updateTeamLocations(teamId, locationIds);
  }
);

export const removeTeamLocation = createAsyncThunk(
  "workOrdersSlice/removeTeamLocation",
  async ({ teamId, locationId }: { teamId: string; locationId: string }) => {
    await teamsApi.removeTeamLocation(teamId, locationId);
  }
);

// ─── Multi-Assignee thunks ──────────────────────────────────

export const addAssignees = createAsyncThunk(
  "workOrdersSlice/addAssignees",
  async ({ workOrderId, request }: { workOrderId: string; request: AddAssigneesRequest }) => {
    return workOrdersApi.addAssignees(workOrderId, request);
  }
);

export const removeAssignee = createAsyncThunk(
  "workOrdersSlice/removeAssignee",
  async ({ workOrderId, userId }: { workOrderId: string; userId: string }) => {
    return workOrdersApi.removeAssignee(workOrderId, userId);
  }
);

export const changeAssigneeRole = createAsyncThunk(
  "workOrdersSlice/changeAssigneeRole",
  async ({
    workOrderId,
    userId,
    request,
  }: {
    workOrderId: string;
    userId: string;
    request: ChangeAssigneeRoleRequest;
  }) => {
    return workOrdersApi.changeAssigneeRole(workOrderId, userId, request);
  }
);

export const assignTeam = createAsyncThunk(
  "workOrdersSlice/assignTeam",
  async ({ workOrderId, request }: { workOrderId: string; request: AssignTeamRequest }) => {
    return workOrdersApi.assignTeam(workOrderId, request);
  }
);

export const reorderWorkOrders = createAsyncThunk(
  "workOrdersSlice/reorderWorkOrders",
  async ({ statusId, workOrderIds }: { statusId: number; workOrderIds: string[] }) => {
    await workOrdersApi.reorderWorkOrders(statusId, workOrderIds);
  }
);

// ─── Initial state ────────────────────────────────────────────

const initialState: WorkOrdersState = {
  workOrders: [],
  totalCount: 0,
  loading: false,
  currentWorkOrder: null,
  currentLoading: false,
  summary: null,
  filters: null,
  myTasks: [],
  myTasksLoading: false,
  recurrencePreview: [],
  generatingRecurring: false,
  teams: [],
  teamsLoading: false,
  teamWorkload: null,
  assignableUsers: [],
  assignableUsersLoading: false,
  analytics: null,
  analyticsLoading: false,
  scheduleWorkOrders: [],
  scheduleLoading: false,
  kpiSummary: null,
  kpiLoading: false,
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────

const workOrdersSlice = createSlice({
  name: "workOrdersSlice",
  initialState,
  reducers: {
    clearCurrentWorkOrder: (state) => {
      state.currentWorkOrder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    optimisticStatusChange: (state, action: PayloadAction<{ id: string; newStatusId: number }>) => {
      const wo = state.workOrders.find(w => w.id === action.payload.id);
      if (wo) {
        wo.workOrderStatusId = action.payload.newStatusId;
      }
    },
    revertStatusChange: (state, action: PayloadAction<{ id: string; oldStatusId: number }>) => {
      const wo = state.workOrders.find(w => w.id === action.payload.id);
      if (wo) {
        wo.workOrderStatusId = action.payload.oldStatusId;
      }
    },
    optimisticAddAssignee: (
      state,
      action: PayloadAction<{ workOrderId: string; userId: string; userName: string }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        const existing = wo.assignees || [];
        // Don't add if already assigned
        if (!existing.some(a => a.userId === action.payload.userId)) {
          const isFirst = existing.length === 0;
          wo.assignees = [
            ...existing,
            {
              userId: action.payload.userId,
              userName: action.payload.userName,
              role: isFirst ? 2 : 1, // First assignee becomes Lead
              roleName: isFirst ? "Lead" : "Assignee",
              assignedAt: new Date().toISOString(),
            },
          ];
          // Update legacy fields if first assignee
          if (isFirst) {
            wo.assignedToUserId = action.payload.userId;
            wo.assignedToUserName = action.payload.userName;
          }
        }
      }
    },
    revertAddAssignee: (
      state,
      action: PayloadAction<{ workOrderId: string; userId: string }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo && wo.assignees) {
        wo.assignees = wo.assignees.filter(a => a.userId !== action.payload.userId);
        // Revert legacy fields if no assignees left
        if (wo.assignees.length === 0) {
          wo.assignedToUserId = undefined;
          wo.assignedToUserName = undefined;
        }
      }
    },
    optimisticSetTeam: (
      state,
      action: PayloadAction<{ workOrderId: string; teamId: string }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.teamId = action.payload.teamId;
      }
    },
    revertSetTeam: (
      state,
      action: PayloadAction<{ workOrderId: string; oldTeamId?: string }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.teamId = action.payload.oldTeamId;
      }
    },
    clearAssignees: (state, action: PayloadAction<{ workOrderId: string }>) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.assignees = [];
        wo.assignedToUserId = undefined;
        wo.assignedToUserName = undefined;
      }
    },
    optimisticReorder: (
      state,
      action: PayloadAction<{ orderedIds: string[] }>
    ) => {
      const { orderedIds } = action.payload;
      orderedIds.forEach((id, index) => {
        const wo = state.workOrders.find(w => w.id === id);
        if (wo) {
          wo.boardPosition = index * 10;
        }
      });
    },
    incrementMemberWorkload: (
      state,
      action: PayloadAction<{ userId: string }>
    ) => {
      const { userId } = action.payload;
      for (const team of state.teams) {
        if (team.members) {
          const member = team.members.find(m => m.userId === userId);
          if (member) {
            member.openWorkOrderCount = (member.openWorkOrderCount || 0) + 1;
          }
        }
      }
    },
    decrementMemberWorkload: (
      state,
      action: PayloadAction<{ userId: string }>
    ) => {
      const { userId } = action.payload;
      for (const team of state.teams) {
        if (team.members) {
          const member = team.members.find(m => m.userId === userId);
          if (member && (member.openWorkOrderCount || 0) > 0) {
            member.openWorkOrderCount = (member.openWorkOrderCount || 0) - 1;
          }
        }
      }
    },
    optimisticSetMachine: (
      state,
      action: PayloadAction<{ workOrderId: string; machineId: string; machineName: string }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.machineId = action.payload.machineId;
        wo.machineName = action.payload.machineName;
      }
    },
    revertSetMachine: (
      state,
      action: PayloadAction<{ workOrderId: string; oldMachineId?: string; oldMachineName?: string }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.machineId = action.payload.oldMachineId;
        wo.machineName = action.payload.oldMachineName;
      }
    },
    optimisticSetLocation: (
      state,
      action: PayloadAction<{
        workOrderId: string;
        locationId: string;
        locationName: string;
        parentLocationName?: string;
      }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.locationId = action.payload.locationId;
        wo.locationName = action.payload.locationName;
        wo.parentLocationName = action.payload.parentLocationName;
      }
    },
    revertSetLocation: (
      state,
      action: PayloadAction<{
        workOrderId: string;
        oldLocationId?: string;
        oldLocationName?: string;
        oldParentLocationName?: string;
      }>
    ) => {
      const wo = state.workOrders.find(w => w.id === action.payload.workOrderId);
      if (wo) {
        wo.locationId = action.payload.oldLocationId;
        wo.locationName = action.payload.oldLocationName;
        wo.parentLocationName = action.payload.oldParentLocationName;
      }
    },
    optimisticCrossColumnMove: (
      state,
      action: PayloadAction<{
        workOrderId: string;
        newStatusId: number;
        targetColumnStatusCodes: number[];
        destinationIndex: number;
      }>
    ) => {
      const { workOrderId, newStatusId, targetColumnStatusCodes, destinationIndex } = action.payload;
      const wo = state.workOrders.find(w => w.id === workOrderId);
      if (!wo) return;

      // Change the WO's status so it appears in the target column
      wo.workOrderStatusId = newStatusId;

      // Collect all WOs in the target column (excluding the moved one, which is now in it)
      // and sort them by boardPosition to get the current visual order
      const targetItems = state.workOrders
        .filter(w => w.id !== workOrderId && targetColumnStatusCodes.includes(w.workOrderStatusId))
        .sort((a, b) => {
          const posA = a.boardPosition ?? Infinity;
          const posB = b.boardPosition ?? Infinity;
          if (posA !== posB) return posA - posB;
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });

      // Insert the moved WO at the destination index
      targetItems.splice(destinationIndex, 0, wo);

      // Reassign boardPosition for all items in the target column
      targetItems.forEach((item, idx) => {
        item.boardPosition = idx * 10;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // ─── fetchWorkOrders ────────────────────────────
      .addCase(fetchWorkOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkOrders.fulfilled, (state, { payload }) => {
        state.workOrders = payload.items;
        state.totalCount = payload.totalCount;
        state.loading = false;
      })
      .addCase(fetchWorkOrders.rejected, (state, { error }) => {
        state.error = error.message || "Failed to load work orders";
        state.loading = false;
      })

      // ─── fetchWorkOrder ─────────────────────────────
      .addCase(fetchWorkOrder.pending, (state) => {
        state.currentLoading = true;
      })
      .addCase(fetchWorkOrder.fulfilled, (state, { payload }) => {
        state.currentWorkOrder = payload;
        state.currentLoading = false;
      })
      .addCase(fetchWorkOrder.rejected, (state, { error }) => {
        state.error = error.message || "Failed to load work order";
        state.currentLoading = false;
      })

      // ─── createWorkOrder ────────────────────────────
      .addCase(createWorkOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkOrder.fulfilled, (state, { payload }) => {
        state.currentWorkOrder = payload;
        state.loading = false;
      })
      .addCase(createWorkOrder.rejected, (state, { error }) => {
        state.error = error.message || "Failed to create work order";
        state.loading = false;
      })

      // ─── updateWorkOrder ────────────────────────────
      .addCase(updateWorkOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkOrder.fulfilled, (state, { payload }) => {
        state.currentWorkOrder = payload;
        state.loading = false;
      })
      .addCase(updateWorkOrder.rejected, (state, { error }) => {
        state.error = error.message || "Failed to update work order";
        state.loading = false;
      })

      // ─── changeWorkOrderStatus ──────────────────────
      .addCase(changeWorkOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeWorkOrderStatus.fulfilled, (state, { payload }) => {
        state.currentWorkOrder = payload;
        state.loading = false;
      })
      .addCase(changeWorkOrderStatus.rejected, (state, { error }) => {
        state.error = error.message || "Failed to change status";
        state.loading = false;
      })

      // ─── editCompletionDetails ──────────────────────
      // Server is source of truth; detail cache is invalidated by the caller
      // (loadDetail) on fulfilled. Clear the matching currentWorkOrder so the
      // stale optimistic view doesn't flash while the refetch is in flight.
      .addCase(editCompletionDetails.pending, (state) => {
        state.error = null;
      })
      .addCase(editCompletionDetails.rejected, (state, { error }) => {
        state.error = error.message || "Failed to update closing details";
      })

      // ─── assignWorkOrder ────────────────────────────
      .addCase(assignWorkOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignWorkOrder.fulfilled, (state, { payload }) => {
        state.currentWorkOrder = payload;
        state.loading = false;
      })
      .addCase(assignWorkOrder.rejected, (state, { error }) => {
        state.error = error.message || "Failed to assign work order";
        state.loading = false;
      })

      // ─── addWorkOrderProgressUpdate ─────────────────
      .addCase(addWorkOrderProgressUpdate.pending, (state) => {
        state.error = null;
      })
      .addCase(addWorkOrderProgressUpdate.fulfilled, (state, { payload }) => {
        if (state.currentWorkOrder) {
          state.currentWorkOrder.progressUpdates = [
            ...state.currentWorkOrder.progressUpdates,
            payload,
          ];
        }
      })
      .addCase(addWorkOrderProgressUpdate.rejected, (state, { error }) => {
        state.error = error.message || "Failed to add progress update";
      })

      // ─── fetchWorkOrderSummary ──────────────────────
      .addCase(fetchWorkOrderSummary.fulfilled, (state, { payload }) => {
        state.summary = payload;
      })

      // ─── fetchWorkOrderFilters ──────────────────────
      .addCase(fetchWorkOrderFilters.fulfilled, (state, { payload }) => {
        state.filters = payload;
      })

      // ─── fetchMyTasks ───────────────────────────────
      .addCase(fetchMyTasks.pending, (state) => {
        state.myTasksLoading = true;
      })
      .addCase(fetchMyTasks.fulfilled, (state, { payload }) => {
        state.myTasks = payload;
        state.myTasksLoading = false;
      })
      .addCase(fetchMyTasks.rejected, (state, { error }) => {
        state.error = error.message || "Failed to load tasks";
        state.myTasksLoading = false;
      })

      // ─── generateRecurring ────────────────────────
      .addCase(generateRecurring.pending, (state) => {
        state.generatingRecurring = true;
      })
      .addCase(generateRecurring.fulfilled, (state) => {
        state.generatingRecurring = false;
      })
      .addCase(generateRecurring.rejected, (state) => {
        state.generatingRecurring = false;
      })

      // ─── previewRecurrence ────────────────────────
      .addCase(previewRecurrence.fulfilled, (state, { payload }) => {
        state.recurrencePreview = payload.occurrences;
      })
      .addCase(previewRecurrence.rejected, (state) => {
        state.recurrencePreview = [];
      })

      // ─── Phase 3B: Teams ──────────────────────────
      .addCase(fetchTeams.pending, (state) => {
        state.teamsLoading = true;
      })
      .addCase(fetchTeams.fulfilled, (state, { payload }) => {
        state.teams = payload;
        state.teamsLoading = false;
      })
      .addCase(fetchTeams.rejected, (state, { error }) => {
        state.error = error.message || "Failed to load teams";
        state.teamsLoading = false;
      })

      .addCase(createTeam.fulfilled, (state, { payload }) => {
        state.teams.push(payload);
      })

      .addCase(updateTeam.fulfilled, (state, { payload }) => {
        // payload may be empty when backend returns Ok() without body
        if (payload && payload.id) {
          const idx = state.teams.findIndex(t => t.id === payload.id);
          if (idx >= 0) state.teams[idx] = payload;
        }
        // Full list is always re-fetched via fetchTeams() in the component
      })

      .addCase(deleteTeam.fulfilled, (state, { payload }) => {
        state.teams = state.teams.filter(t => t.id !== payload);
      })

      .addCase(fetchTeamWorkload.fulfilled, (state, { payload }) => {
        state.teamWorkload = payload;
      })
      .addCase(fetchTeamWorkload.rejected, (state) => {
        state.teamWorkload = null;
      })

      // ─── RC-5: Assignable Users ──────────────────────
      .addCase(fetchAssignableUsers.pending, (state) => {
        state.assignableUsersLoading = true;
      })
      .addCase(fetchAssignableUsers.fulfilled, (state, { payload }) => {
        state.assignableUsers = payload;
        state.assignableUsersLoading = false;
      })
      .addCase(fetchAssignableUsers.rejected, (state) => {
        state.assignableUsersLoading = false;
      })

      // ─── Phase 3A: Schedule ──────────────────────
      .addCase(fetchScheduleWorkOrders.pending, (state) => {
        state.scheduleLoading = true;
      })
      .addCase(fetchScheduleWorkOrders.fulfilled, (state, { payload }) => {
        state.scheduleWorkOrders = payload.items;
        state.scheduleLoading = false;
      })
      .addCase(fetchScheduleWorkOrders.rejected, (state) => {
        state.scheduleLoading = false;
      })

      // ─── Phase 3C: Analytics ──────────────────────
      .addCase(fetchAnalytics.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(fetchAnalytics.fulfilled, (state, { payload }) => {
        state.analytics = payload;
        state.analyticsLoading = false;
      })
      .addCase(fetchAnalytics.rejected, (state, { error }) => {
        state.error = error.message || "Failed to load analytics";
        state.analyticsLoading = false;
      })

      // ─── KPI Summary ────────────────────────────────
      .addCase(fetchKpiSummary.pending, (state) => {
        state.kpiLoading = true;
      })
      .addCase(fetchKpiSummary.fulfilled, (state, { payload }) => {
        state.kpiSummary = payload;
        state.kpiLoading = false;
      })
      .addCase(fetchKpiSummary.rejected, (state, { error }) => {
        console.error("[WorkOrders] fetchKpiSummary failed:", error?.message || error);
        state.kpiLoading = false;
        // Fallback to zeros so the UI shows "0" instead of "--" forever
        if (!state.kpiSummary) {
          state.kpiSummary = {
            activeCount: 0,
            criticalCount: 0,
            overdueCount: 0,
            avgResolutionDays: 0,
            doneThisWeekCount: 0,
            doneThisWeekTotal: 0,
          };
        }
      })

      // ─── Multi-Assignee ────────────────────────────────
      .addCase(addAssignees.fulfilled, (state, { payload }) => {
        // Update the WO in the board list with new assignees
        const idx = state.workOrders.findIndex(w => w.id === payload.id);
        if (idx >= 0) {
          state.workOrders[idx].assignees = payload.assignees;
          state.workOrders[idx].assignedToUserId = payload.assignedToUserId;
          state.workOrders[idx].assignedToUserName = payload.assignedToUserName;
        }
        if (state.currentWorkOrder?.id === payload.id) {
          state.currentWorkOrder = payload;
        }
      })
      .addCase(addAssignees.rejected, (state, { error }) => {
        state.error = error.message || "Failed to add assignees";
      })

      .addCase(removeAssignee.fulfilled, (state, { payload }) => {
        const idx = state.workOrders.findIndex(w => w.id === payload.id);
        if (idx >= 0) {
          state.workOrders[idx].assignees = payload.assignees;
          state.workOrders[idx].assignedToUserId = payload.assignedToUserId;
          state.workOrders[idx].assignedToUserName = payload.assignedToUserName;
        }
        if (state.currentWorkOrder?.id === payload.id) {
          state.currentWorkOrder = payload;
        }
      })
      .addCase(removeAssignee.rejected, (state, { error }) => {
        state.error = error.message || "Failed to remove assignee";
      })

      .addCase(changeAssigneeRole.fulfilled, (state, { payload }) => {
        const idx = state.workOrders.findIndex(w => w.id === payload.id);
        if (idx >= 0) {
          state.workOrders[idx].assignees = payload.assignees;
          state.workOrders[idx].assignedToUserId = payload.assignedToUserId;
          state.workOrders[idx].assignedToUserName = payload.assignedToUserName;
        }
        if (state.currentWorkOrder?.id === payload.id) {
          state.currentWorkOrder = payload;
        }
      })

      .addCase(assignTeam.fulfilled, (state, { payload }) => {
        const idx = state.workOrders.findIndex(w => w.id === payload.id);
        if (idx >= 0) {
          state.workOrders[idx].teamId = payload.teamId;
        }
        if (state.currentWorkOrder?.id === payload.id) {
          state.currentWorkOrder = payload;
        }
      })
      .addCase(assignTeam.rejected, (state, { error }) => {
        state.error = error.message || "Failed to assign team";
      });
  },
});

export const {
  clearCurrentWorkOrder,
  clearError,
  optimisticStatusChange,
  revertStatusChange,
  optimisticAddAssignee,
  revertAddAssignee,
  optimisticSetTeam,
  revertSetTeam,
  clearAssignees,
  optimisticReorder,
  optimisticCrossColumnMove,
  incrementMemberWorkload,
  decrementMemberWorkload,
  optimisticSetMachine,
  revertSetMachine,
  optimisticSetLocation,
  revertSetLocation,
} = workOrdersSlice.actions;

export default workOrdersSlice.reducer;
